import type { TSplitPayment, TSubscriptionType } from '@/_models';
import { dateTime, getFormattedCurrency, safeFloatParse } from '@/_shared';

export function areSplitPaymentsValid(splitPayments: TSplitPayment[] | null = []) {
  return (splitPayments ?? []).length > 1;
}

export function getFloatInput(
  hasSplitDownPayment: boolean,
  splitPayments: { amount: string; date: string }[],
  wantToPayDownPayment: string
) {
  return hasSplitDownPayment
    ? splitPayments
        .reduce((acc, cur) => acc + safeFloatParse(cur.amount), 0)
        .toFixed(2)
        .replace('.00', '')
    : wantToPayDownPayment;
}

export function termsAprCalculations(
  paymentTerm: number,
  subscriptionType: TSubscriptionType,
  perInstallmentPrice: number,
  investmentAfterSavings: number,
  todaysPayment: number,
  pifInvestmentAfterSavings: number,
  _recurringPaymentStartDate: string,
  splitPayments: TSplitPayment[],
  withSplitDownPayment: number,
  account_charge = 0,
  isClimb = false
) {
  let diff = 0;
  let diffPercentage = 0;

  const fRecurringPaymentStartDate = dateTime.parse(_recurringPaymentStartDate).format('MM/DD/YYYY');
  let isDiffNegative = false;
  let splitPaymentsBreakdown = [] as { name: string; value?: string }[];

  if (subscriptionType !== 'pif') {
    diff = investmentAfterSavings - pifInvestmentAfterSavings;
    isDiffNegative = diff < 0;
    if (isDiffNegative) {
      diff = 0;
    }
    diffPercentage = (1 / paymentTerm) * (investmentAfterSavings / pifInvestmentAfterSavings - 1) * 12 * 100;
  }
  splitPaymentsBreakdown = splitPayments.map((item) => ({
    name: `${getFormattedCurrency(parseFloat(item.amount), true)} on ${dateTime.format(item.date, 'MM/DD/YYYY')}`,
  }));
  const hasSplitPayments = areSplitPaymentsValid(splitPayments);

  let headings =
    subscriptionType === 'pif'
      ? [
          { name: 'Number of payments', value: `${hasSplitPayments ? splitPayments.length : 1}` },
          { name: 'Payment frequency', value: 'Pay in full' },
          hasSplitPayments
            ? {
                name: 'Payment Details',
                value: '',
                list: hasSplitPayments ? splitPaymentsBreakdown : undefined,
              }
            : { name: 'Payment Date', value: fRecurringPaymentStartDate },
          { name: 'Payment Amount', value: getFormattedCurrency(todaysPayment, true) },
          { name: 'Total investment', value: getFormattedCurrency(investmentAfterSavings, true) },
        ]
      : [
          {
            name: 'Down payment',
            value: getFormattedCurrency(withSplitDownPayment, true),
            list: splitPaymentsBreakdown,
          },
          { name: 'Number of payments', value: paymentTerm },
          { name: 'Payment frequency', value: subscriptionType },
          { name: 'Recurring Payment Start', value: fRecurringPaymentStartDate },
          { name: 'Recurring Payment Amount', value: getFormattedCurrency(perInstallmentPrice, true) },
          account_charge ? { name: 'Account Charge', value: getFormattedCurrency(account_charge, true) } : undefined,
          { name: 'Total investment', value: getFormattedCurrency(investmentAfterSavings, true) },
        ];

  if (isClimb) {
    const cHeadings = [
      headings[0],
      { name: 'Payment Amount', value: getFormattedCurrency(investmentAfterSavings - withSplitDownPayment, true) },
      { name: 'Total investment', value: getFormattedCurrency(investmentAfterSavings, true) },
    ];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    headings = [...cHeadings];
  }

  return { headings, isDiffNegative, diffPercentage, diff };
}

export function getInitials(firstName: string, lastName: string) {
  if (!firstName || !lastName) return '';

  const f = firstName.split('')[0];
  const l = lastName.split('')[0];

  return (f + l)?.toUpperCase();
}
